import { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { IconInbox, IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { useInfiniteCorrectionRequestsWorkQueue } from "../../hooks/useCorrectionRequests";
import { usePathologistQueueDefaultPreference } from "../../hooks/usePathologistQueueDefaultPreference";
import { advanceReviewSorts } from "./pathologistReviewQueueView";
import {
  applyPathologistQueueDefault,
  createPathologistQueueDefault,
  decodePathologistQueueDefault,
} from "./pathologistQueueDefaultPreference";
import type { PathologistQueueTabView } from "./pathologistQueueViewPreference";

const SORT_COLUMNS = [
  { id: "caseNumber", label: "Case #" },
  { id: "patient", label: "Patient" },
  { id: "requestor", label: "Requestor" },
  { id: "fieldCount", label: "Changed Fields" },
];

interface CorrectionRequestsTabProps {
  view: PathologistQueueTabView;
  onViewChange: (view: PathologistQueueTabView) => void;
  onClearView: () => void;
  active: boolean;
  hydrated: boolean;
  invalid: boolean;
}

export function CorrectionRequestsTab({
  view,
  onViewChange,
  onClearView,
  active,
  hydrated,
  invalid,
}: CorrectionRequestsTabProps) {
  const navigate = useNavigate();
  const filters = view.filters;
  const [debouncedSearch] = useDebouncedValue(filters.search ?? "", 300);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [defaultMessage, setDefaultMessage] = useState<string | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [defaultSaving, setDefaultSaving] = useState(false);
  const { getRawDefault, hydratedTabs, errorTabs, saveDefault } =
    usePathologistQueueDefaultPreference();
  const rawDefault = getRawDefault("corrections");
  const sorts = useMemo(
    () => view.sorts ?? (view.sort ? [view.sort] : []),
    [view.sort, view.sorts]
  );
  const queryFilters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      prefixKeys: filters.prefixKeys,
      sorts,
    }),
    [debouncedSearch, filters.prefixKeys, sorts]
  );
  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteCorrectionRequestsWorkQueue(queryFilters, {
    enabled: active,
  });
  const items = useMemo(
    () => data?.pages.flatMap((result) => result.items) ?? [],
    [data?.pages]
  );
  const total = useMemo(() => data?.pages[0]?.total ?? 0, [data?.pages]);
  const prefixOptions = useMemo(
    () => data?.pages[0]?.prefixOptions ?? [],
    [data?.pages]
  );
  const savedDefault = useMemo(
    () =>
      decodePathologistQueueDefault(rawDefault, prefixOptions, "corrections"),
    [rawDefault, prefixOptions]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onViewChange({
        ...view,
        filters: { ...filters, search: event.currentTarget.value || undefined },
        page: 1,
      });
    },
    [filters, onViewChange, view]
  );
  const handlePrefixChange = useCallback(
    (selected: string[]) => {
      const unavailable = (filters.prefixKeys ?? []).filter(
        (prefix) => !prefixOptions.includes(prefix)
      );
      const prefixKeys = Array.from(new Set([...unavailable, ...selected]));
      onViewChange({
        ...view,
        filters: {
          ...filters,
          prefixKeys: prefixKeys.length ? prefixKeys : undefined,
        },
        page: 1,
      });
    },
    [filters, onViewChange, prefixOptions, view]
  );
  const handleSort = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const column = event.currentTarget.dataset.column;
      if (!column) return;
      onViewChange({
        ...view,
        sort: null,
        sorts: advanceReviewSorts(sorts, column),
        page: 1,
      });
    },
    [onViewChange, sorts, view]
  );
  const handleOpenCase = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const { navigateUrl, caseId } = event.currentTarget.dataset;
      if (caseId) navigate(navigateUrl || `/cases/${caseId}`);
    },
    [navigate]
  );
  const handleSaveDefault = useCallback(async () => {
    setDefaultError(null);
    setDefaultMessage(null);
    const snapshot = createPathologistQueueDefault(
      view,
      prefixOptions,
      "corrections"
    );
    if (!snapshot) {
      setDefaultError(
        "This view cannot be saved until its prefix choices are available and valid."
      );
      return;
    }
    setDefaultSaving(true);
    try {
      await saveDefault("corrections", snapshot);
      setDefaultMessage("Default saved. Your current view has not changed.");
    } catch {
      setDefaultError("The default could not be saved. Please try again.");
    } finally {
      setDefaultSaving(false);
    }
  }, [prefixOptions, saveDefault, view]);
  const handleApplyDefault = useCallback(() => {
    setDefaultError(null);
    setDefaultMessage(null);
    if (!savedDefault) {
      setDefaultError(
        "The saved default is unavailable or no longer valid for this queue."
      );
      return;
    }
    onViewChange(
      applyPathologistQueueDefault(view, savedDefault, "corrections")
    );
    setDefaultMessage("Default applied to your current view.");
  }, [onViewChange, savedDefault, view]);
  const handleClearView = useCallback(() => onClearView(), [onClearView]);
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  const attachLoadBoundary = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (
        !node ||
        !active ||
        !hasNextPage ||
        isFetchingNextPage ||
        typeof IntersectionObserver === "undefined"
      )
        return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          handleLoadMore();
        },
        { rootMargin: "0px 0px 160px 0px" }
      );
      observerRef.current = observer;
      observer.observe(node);
    },
    [active, handleLoadMore, hasNextPage, isFetchingNextPage]
  );

  return (
    <Tabs.Panel value="corrections" pt="sm">
      <Stack gap="sm">
        <Group gap="xs">
          <Text fw={600} size="lg">
            Pending Correction Requests
          </Text>
          {total > 0 && (
            <Badge color="orange" size="sm">
              {total}
            </Badge>
          )}
        </Group>
        <Group align="flex-start" grow>
          <Paper p="xs" withBorder style={{ flex: 2 }}>
            <Group gap="xs">
              <IconSearch size={18} />
              <TextInput
                aria-label="Search correction requests"
                placeholder="Search by case # or patient name"
                value={filters.search ?? ""}
                onChange={handleSearchChange}
                style={{ flex: 1 }}
              />
            </Group>
          </Paper>
          <Paper p="xs" withBorder style={{ flex: 1 }}>
            <>
              <Group gap="xs" wrap="wrap">
                <Button
                  size="compact-sm"
                  variant="subtle"
                  onClick={handleSaveDefault}
                  loading={defaultSaving}
                  disabled={
                    !hydrated ||
                    invalid ||
                    !data ||
                    !hydratedTabs.corrections ||
                    errorTabs.corrections
                  }
                >
                  Save Default
                </Button>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  onClick={handleApplyDefault}
                  disabled={
                    !hydrated ||
                    invalid ||
                    !data ||
                    !hydratedTabs.corrections ||
                    !savedDefault
                  }
                >
                  Apply Default
                </Button>
                {(filters.search || filters.prefixKeys?.length) && (
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    onClick={handleClearView}
                  >
                    Clear Filters
                  </Button>
                )}
              </Group>
              {prefixOptions.length > 0 && (
                <Checkbox.Group
                  label="Case prefixes"
                  value={filters.prefixKeys ?? []}
                  onChange={handlePrefixChange}
                  mt="xs"
                >
                  <Group gap="xs" mt={4}>
                    {prefixOptions.map((prefix) => (
                      <Checkbox
                        key={prefix}
                        value={prefix}
                        size="xs"
                        label={
                          prefix === "__OTHER__"
                            ? "Other / nonstandard number"
                            : prefix
                        }
                      />
                    ))}
                  </Group>
                </Checkbox.Group>
              )}
              <Text size="xs" c="dimmed" mt="xs">
                Defaults exclude free-text search.
              </Text>
              {(defaultError ||
                errorTabs.corrections ||
                (data && rawDefault && !savedDefault)) && (
                <Alert
                  color="red"
                  variant="light"
                  role="alert"
                  title="Default unavailable"
                  mt="xs"
                >
                  {defaultError ??
                    "The saved default could not be loaded or is no longer valid."}
                </Alert>
              )}
              {defaultMessage && (
                <Text size="xs" role="status" mt="xs">
                  {defaultMessage}
                </Text>
              )}
            </>
          </Paper>
        </Group>
        {isError && (
          <Alert
            color="red"
            variant="light"
            role="alert"
            title="Requests unavailable"
          >
            The correction requests could not be loaded.
            <Button size="compact-sm" variant="subtle" onClick={handleRetry}>
              Retry
            </Button>
          </Alert>
        )}
        {isLoading && !data ? (
          <Text size="sm" c="dimmed" role="status">
            Loading correction requests…
          </Text>
        ) : isError && items.length === 0 ? null : items.length === 0 ? (
          <Paper p="md" withBorder>
            <Stack align="center" gap="sm" py="xl">
              <IconInbox size={36} color="var(--mantine-color-gray-5)" />
              <Text fw={500} ta="center">
                {filters.search || filters.prefixKeys?.length
                  ? "No correction requests match these filters."
                  : "No pending correction requests."}
              </Text>
            </Stack>
          </Paper>
        ) : (
          <Paper p="xs" withBorder style={{ minWidth: 0, maxWidth: "100%" }}>
            <>
              <Table.ScrollContainer minWidth={720} type="native">
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      {SORT_COLUMNS.map((column) => {
                        const sort = sorts.find(
                          (entry) => entry.column === column.id
                        );
                        const direction =
                          sort?.direction === "asc"
                            ? "ascending"
                            : sort?.direction === "desc"
                            ? "descending"
                            : "none";
                        return (
                          <Table.Th key={column.id} aria-sort={direction}>
                            <Button
                              variant="subtle"
                              size="compact-sm"
                              color="dark"
                              data-column={column.id}
                              onClick={handleSort}
                            >
                              {column.label}
                              {sort
                                ? ` ${sort.direction === "asc" ? "↑" : "↓"}`
                                : ""}
                            </Button>
                          </Table.Th>
                        );
                      })}
                      <Table.Th>Note</Table.Th>
                      <Table.Th>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {items.map((item) => (
                      <Table.Tr key={item.correctionRequestId ?? item.caseId}>
                        <Table.Td>{item.caseNumber}</Table.Td>
                        <Table.Td>{item.patientName}</Table.Td>
                        <Table.Td>{item.requestorName || "—"}</Table.Td>
                        <Table.Td>{item.proposedFieldCount ?? 0}</Table.Td>
                        <Table.Td>{item.hasNote ? "Has note" : "—"}</Table.Td>
                        <Table.Td>
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            data-navigate-url={item.navigateUrl}
                            data-case-id={item.caseId}
                            onClick={handleOpenCase}
                          >
                            Open case
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </>
          </Paper>
        )}
        {items.length > 0 && (
          <Stack gap="xs" align="center">
            <Text size="sm" c="dimmed" role="status" aria-live="polite">
              {hasNextPage
                ? `Showing ${items.length} of ${total} requests`
                : `All ${items.length} requests shown`}
            </Text>
            {hasNextPage && (
              <>
                <Box ref={attachLoadBoundary} h={1} aria-hidden="true" />
                <Button
                  size="sm"
                  variant="light"
                  onClick={handleLoadMore}
                  loading={isFetchingNextPage}
                >
                  Load more requests
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Tabs.Panel>
  );
}
